import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  Film,
  Globe,
  Home,
  Image,
  Layers,
  MessageSquare,
  Play,
  Share2,
  Sparkles,
  Upload,
  Users,
  Wand2,
  Zap,
  Gift,
  DollarSign,
  TrendingUp,
  Award
} from "lucide-react";

// Import Flow logo and cover photo
import flowLogo from '../../Gallery/Flow Logo.png';
import flowCoverPhoto from '../../Gallery/Flow Cover Photo.png';

// Import sample images from MakeWithFlow
import livingroomBefore from '../../VirtualStaging/livingroom-before.jpg';
import livingroomAfter from '../../VirtualStaging/livingroom-after.jpg';
import bedroomBefore from '../../VirtualStaging/bedroom-before.png';
import bedroomAfter from '../../VirtualStaging/bedroom-after.png';

// Video reel data - YouTube Shorts embeds
const VIDEO_REELS = [
  { id: 1, youtubeId: "DCf2zvytyfk", title: "Modern Living Room", style: "Editorial Style" },
  { id: 2, youtubeId: "OJyq--Ci8qI", title: "Cozy Bedroom", style: "Warm Scandinavian" },
  { id: 3, youtubeId: "yPakqKTNEms", title: "Reveal Effect", style: "Dramatic Transition" }
];

// Feature menu items for dropdown navigation
const FEATURE_MENU_ITEMS = [
  { label: "AI Virtual Staging", href: "/flow/features/virtual-staging", icon: "🏠", description: "Transform empty rooms with AI" },
  { label: "Video Reels", href: "/flow/features/video-reels", icon: "🎬", description: "Create TikTok & IG reels in seconds" },
  { label: "Batch Processing", href: "/flow/features/batch-processing", icon: "📸", description: "Process multiple photos at once" },
  { label: "Landing Pages", href: "/flow/features/landing-pages", icon: "🌐", description: "Beautiful property galleries" },
  { label: "Agent Portfolio", href: "/flow/features/portfolio", icon: "✨", description: "Your branded agent page" },
  { label: "Refer & Earn", href: "/flow/features/refer-earn", icon: "💰", description: "Earn credits for referrals" }
];

// Shared Navigation Component with Features Dropdown
const FeatureNavigation = () => {
  const [isSolid, setIsSolid] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsSolid(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFeaturesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get current page to highlight in dropdown
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

  return (
    <motion.nav
      initial={{ y: -96, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isSolid ? "border-b border-slate-700 bg-[#0d1420]/95 shadow-lg backdrop-blur-xl" : "bg-[#0a0f1a]"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <a href="/flow" className="flex items-center gap-3">
          <img src={flowLogo} alt="Flow" className="h-11 w-11 rounded-xl shadow-[0_10px_30px_rgba(20,244,204,0.25)]" />
          <div>
            <div className="text-lg font-semibold text-white">Flow</div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/60">Virtual Staging</div>
          </div>
        </a>

        <div className="hidden items-center gap-6 md:flex">
          <a href="/flow" className="text-sm font-medium text-white/70 hover:text-white">
            Home
          </a>

          {/* Features Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setFeaturesOpen(!featuresOpen)}
              className="flex items-center gap-1 text-sm font-medium transition-colors text-white/70 hover:text-white"
            >
              Features
              <ChevronDown className={`h-4 w-4 transition-transform ${featuresOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown Menu */}
            {featuresOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                className="absolute left-1/2 top-full mt-3 w-72 -translate-x-1/2 rounded-2xl border border-slate-700 bg-[#0d1420] p-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
              >
                {FEATURE_MENU_ITEMS.map((item) => {
                  const isActive = currentPath === item.href;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setFeaturesOpen(false)}
                      className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                        isActive ? "bg-[#5eead4]/10" : "hover:bg-slate-800"
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <div>
                        <div className={`text-sm font-semibold ${isActive ? "text-[#5eead4]" : "text-white"}`}>
                          {item.label}
                          {isActive && <span className="ml-2 text-xs text-[#5eead4]">•</span>}
                        </div>
                        <div className="text-xs text-slate-400">{item.description}</div>
                      </div>
                    </a>
                  );
                })}
                <div className="mt-2 border-t border-slate-700 pt-2">
                  <a
                    href="/flow#pricing"
                    onClick={() => setFeaturesOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#5eead4] transition-colors hover:bg-[#5eead4]/10"
                  >
                    <ArrowRight className="h-4 w-4" />
                    View Pricing
                  </a>
                </div>
              </motion.div>
            )}
          </div>

          <a href="/flow#pricing" className="text-sm font-medium text-white/70 hover:text-white">
            Pricing
          </a>
          <a
            href="/?login=true"
            className="rounded-full bg-[#5eead4] px-5 py-2.5 text-sm font-semibold text-[#0a0f1a] transition-transform hover:-translate-y-0.5"
          >
            Get Started
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

// Shared Footer
const FeatureFooter = () => (
  <footer className="bg-[#0a0f1a] px-6 py-16">
    <div className="mx-auto max-w-7xl text-center">
      <h2 className="text-3xl font-semibold text-white md:text-4xl">
        Ready to transform your listings?
      </h2>
      <p className="mx-auto mt-4 max-w-xl text-white/60">
        Join thousands of agents using Flow to sell homes faster.
      </p>
      <a
        href="/?login=true"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#5eead4] px-8 py-4 text-lg font-semibold text-[#0a0f1a] transition-transform hover:-translate-y-1"
      >
        Start Free Today
        <ArrowRight className="h-5 w-5" />
      </a>
      <p className="mt-6 text-sm text-white/40">
        No credit card required. 5 free staging credits included.
      </p>
    </div>
  </footer>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-6 shadow-sm">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#5eead4]/10">
      <Icon className="h-6 w-6 text-[#5eead4]" />
    </div>
    <h3 className="text-lg font-semibold text-white">{title}</h3>
    <p className="mt-2 text-slate-400">{description}</p>
  </div>
);

// ============================================================================
// VIRTUAL STAGING FEATURE PAGE
// ============================================================================
export const VirtualStagingFeaturePage = () => {
  const [showAfter, setShowAfter] = useState(true);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Cover Photo Hero */}
      <section className="relative pt-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative w-full"
        >
          {/* Cover Image */}
          <div className="relative w-full overflow-hidden">
            <img
              src={flowCoverPhoto}
              alt="Flow - AI Virtual Staging for Real Estate"
              className="w-full h-auto min-h-[300px] md:min-h-[400px] lg:min-h-[500px] object-cover object-center"
            />
            {/* Gradient overlay for seamless blend */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a]/60 via-transparent to-transparent" />
          </div>

          {/* CTA Overlay */}
          <div className="absolute bottom-8 md:bottom-12 left-0 right-0 flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <a
                href="/?login=true"
                className="rounded-full bg-[#5eead4] px-8 py-4 text-lg font-semibold text-[#0a0f1a] transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(94,234,212,0.3)]"
              >
                Start Staging Free
              </a>
              <a
                href="#how-it-works"
                className="flex items-center gap-2 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-white/10"
              >
                <Play className="h-5 w-5" />
                Watch Demo
              </a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Before/After Interactive Section */}
      <section className="relative px-6 py-16 bg-[#0a0f1a]">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See the <span className="text-[#5eead4]">transformation</span>
            </h2>
            <p className="mt-4 text-lg text-white/60 max-w-xl mx-auto">
              From empty room to stunning staged space in seconds
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
              <img
                src={showAfter ? livingroomAfter : livingroomBefore}
                alt={showAfter ? "After staging" : "Before staging"}
                className="w-full transition-opacity duration-500"
              />
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                <div className="flex gap-2 rounded-full bg-black/60 p-1.5 backdrop-blur">
                  <button
                    onClick={() => setShowAfter(false)}
                    className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${!showAfter ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
                  >
                    Before
                  </button>
                  <button
                    onClick={() => setShowAfter(true)}
                    className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${showAfter ? "bg-[#5eead4] text-black" : "text-white/70 hover:text-white"}`}
                  >
                    After
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Story Section */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="prose prose-lg mx-auto text-center"
          >
            <h2 className="text-4xl font-semibold text-white">
              The old way was painful.
            </h2>
            <p className="mt-6 text-xl text-slate-400">
              You'd spend $300-500 per room. Wait 3-5 business days. Go back and forth on revisions.
              And if the style wasn't right? Start over.
            </p>
            <p className="text-xl text-slate-400">
              Your listings sat empty while buyers scrolled past.
            </p>

            <div className="my-12 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

            <h2 className="text-4xl font-semibold text-[#5eead4]">
              Flow changes everything.
            </h2>
            <p className="mt-6 text-xl text-slate-400">
              Upload a photo. Type what you want. Get stunning results in under 30 seconds.
              Don't love it? Try a different style instantly. No waiting. No extra cost.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Three steps. Thirty seconds.
          </h2>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              { step: "01", title: "Upload", desc: "Drag and drop your empty room photo. We accept any standard image format.", icon: Upload },
              { step: "02", title: "Describe", desc: "Tell us what you want. 'Scandinavian minimalist' or 'Luxury transitional' - be as specific as you like.", icon: MessageSquare },
              { step: "03", title: "Transform", desc: "Watch AI work its magic. Download your staged photo or refine it further in Sessions.", icon: Sparkles }
            ].map((item) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl bg-white/5 p-8 backdrop-blur border border-slate-700"
              >
                <div className="text-5xl font-bold text-[#5eead4]/30">{item.step}</div>
                <item.icon className="mt-4 h-8 w-8 text-[#5eead4]" />
                <h3 className="mt-4 text-2xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-white/60">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Why agents love Flow staging
          </h2>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Zap} title="Instant Results" description="No more waiting days. Get beautifully staged photos in under 30 seconds." />
            <FeatureCard icon={MessageSquare} title="Natural Language" description="Just describe what you want. No complex tools or design skills needed." />
            <FeatureCard icon={Layers} title="Unlimited Revisions" description="Don't love it? Try a different style. Refine in Sessions until it's perfect." />
            <FeatureCard icon={DollarSign} title="Fraction of the Cost" description="Traditional staging: $300-500/room. Flow: pennies per image." />
            <FeatureCard icon={Home} title="Every Room Type" description="Living rooms, bedrooms, kitchens, bathrooms, offices - we handle them all." />
            <FeatureCard icon={Download} title="High Resolution" description="Download print-ready images perfect for MLS, flyers, and social media." />
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

// ============================================================================
// VIDEO REELS FEATURE PAGE
// ============================================================================
export const VideoReelsFeaturePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-24 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(168,85,247,0.15),_transparent_40%)]" />

        <div className="relative mx-auto max-w-7xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
              <Film className="h-4 w-4 text-purple-400" />
              Social Media Ready
            </div>

            <h1 className="mt-8 text-5xl font-semibold leading-tight text-white md:text-7xl">
              Scroll-stopping reels.
              <span className="block text-purple-400">Created in seconds.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/60">
              Turn your staged photos into cinematic video reels perfect for Instagram, TikTok,
              and YouTube Shorts. No editing skills required.
            </p>

            <div className="mt-10">
              <a href="/?login=true" className="rounded-full bg-purple-500 px-8 py-4 text-lg font-semibold text-white transition-transform hover:-translate-y-1">
                Create Your First Reel
              </a>
            </div>
          </motion.div>

          {/* Video Preview Grid - YouTube Shorts */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-16 flex justify-center gap-4 overflow-x-auto pb-4"
          >
            {VIDEO_REELS.map((video) => (
              <div key={video.id} className="relative h-96 w-56 flex-shrink-0 overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-white/10">
                <iframe
                  src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${video.youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                  title={video.title}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="h-full w-full object-cover pointer-events-none"
                  style={{ border: 'none' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                  <div className="text-sm font-semibold text-white">{video.title}</div>
                  <div className="text-xs text-purple-300">{video.style}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-semibold text-white">
            Video gets 3x more engagement.
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            Static photos get scrolled past. Video stops the thumb. But who has time to learn
            video editing when you're busy closing deals?
          </p>

          <div className="my-12 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

          <h2 className="text-4xl font-semibold text-purple-400">
            Flow creates reels automatically.
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            Select your staged photos. Choose a style - smooth pan, dramatic reveal, or cinematic zoom.
            Add music. Download. Post. Watch the likes roll in.
          </p>
        </div>
      </section>

      {/* All Video Reels Showcase */}
      <section className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            See Flow Reels in Action
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-white/60">
            Real examples created with Flow's AI video generator
          </p>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {VIDEO_REELS.map((video) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group relative aspect-[9/16] overflow-hidden rounded-2xl bg-black shadow-xl ring-1 ring-white/10"
              >
                <iframe
                  src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${video.youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                  title={video.title}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="h-full w-full object-cover pointer-events-none"
                  style={{ border: 'none' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" />

                {/* Style Badge */}
                <div className="absolute top-4 left-4 pointer-events-none">
                  <span className="rounded-full bg-purple-500/80 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                    {video.style}
                  </span>
                </div>

                {/* Title */}
                <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                  <div className="text-lg font-semibold text-white">{video.title}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Icons */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-semibold text-white">
            Perfect for every platform
          </h2>
          <p className="mt-4 text-xl text-slate-400">
            Export in the right format for Instagram Reels, TikTok, YouTube Shorts, or CapCut
          </p>

          <div className="mt-12 flex justify-center gap-8">
            {["Instagram", "TikTok", "YouTube", "CapCut"].map((platform) => (
              <div key={platform} className="flex flex-col items-center gap-2">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#5eead4]/10">
                  <Film className="h-8 w-8 text-[#5eead4]" />
                </div>
                <span className="text-sm font-medium text-slate-400">{platform}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

// ============================================================================
// BATCH PROCESSING FEATURE PAGE
// ============================================================================
export const BatchProcessingFeaturePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-24 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.15),_transparent_40%)]" />

        <div className="relative mx-auto max-w-7xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
              <Layers className="h-4 w-4 text-emerald-400" />
              Save Hours of Work
            </div>

            <h1 className="mt-8 text-5xl font-semibold leading-tight text-white md:text-7xl">
              48 photos.
              <span className="block text-emerald-400">One click.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/60">
              Upload an entire property at once. Our AI detects each room type, applies the perfect
              staging, and delivers everything in minutes - not days.
            </p>

            <div className="mt-10">
              <a href="/?login=true" className="rounded-full bg-emerald-500 px-8 py-4 text-lg font-semibold text-white transition-transform hover:-translate-y-1">
                Process Your First Batch
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Pain */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-red-500">The Old Way</h3>
              <h2 className="mt-4 text-3xl font-semibold text-white">
                Hours of tedious work
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  "Upload photos one by one",
                  "Manually tag each room type",
                  "Wait for each to process",
                  "Download individually",
                  "Organize into folders"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-400">
                    <div className="h-2 w-2 rounded-full bg-red-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">The Flow Way</h3>
              <h2 className="mt-4 text-3xl font-semibold text-white">
                Minutes of magic
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  "Drag & drop entire folder",
                  "AI detects all room types",
                  "Everything processes in parallel",
                  "Download as organized ZIP",
                  "Ready for MLS upload"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-400">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 md:grid-cols-3 text-center">
            {[
              { value: "48", label: "Photos processed at once" },
              { value: "3 min", label: "Average batch time" },
              { value: "95%", label: "Room detection accuracy" }
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-6xl font-bold text-[#5eead4]">{stat.value}</div>
                <div className="mt-2 text-white/60">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Intelligent batch workflow
          </h2>

          <div className="mt-16 grid gap-8 md:grid-cols-4">
            {[
              { icon: Upload, title: "Upload All", desc: "Drop your entire photo folder" },
              { icon: Sparkles, title: "AI Analyzes", desc: "Rooms auto-detected" },
              { icon: Wand2, title: "Batch Process", desc: "All photos staged in parallel" },
              { icon: Download, title: "Download", desc: "Get organized ZIP file" }
            ].map((step, i) => (
              <div key={step.title} className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10">
                  <step.icon className="h-8 w-8 text-emerald-400" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-slate-400">{step.desc}</p>
                {i < 3 && <ArrowRight className="mx-auto mt-4 h-6 w-6 text-slate-600 hidden md:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

// ============================================================================
// LANDING PAGES FEATURE PAGE
// ============================================================================
export const LandingPagesFeaturePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-24 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(99,102,241,0.15),_transparent_40%)]" />

        <div className="relative mx-auto max-w-7xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
              <Globe className="h-4 w-4 text-indigo-400" />
              Your Brand, Your Leads
            </div>

            <h1 className="mt-8 text-5xl font-semibold leading-tight text-white md:text-7xl">
              Stop sending traffic
              <span className="block text-indigo-400">to Zillow.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/60">
              Auto-generate stunning property landing pages with your branding, your contact info,
              and lead capture forms. Every lead goes directly to you.
            </p>

            <div className="mt-10">
              <a href="/?login=true" className="rounded-full bg-indigo-500 px-8 py-4 text-lg font-semibold text-white transition-transform hover:-translate-y-1">
                Create Your First Gallery
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Problem */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-semibold text-white">
            Where do your leads really go?
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            When you share a Zillow or Realtor.com link, buyers see other agents.
            Other listings. Your hard-earned traffic becomes someone else's lead.
          </p>

          <div className="my-12 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

          <h2 className="text-4xl font-semibold text-indigo-400">
            Your listing. Your page. Your leads.
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            Flow landing pages feature YOUR photo, YOUR contact info, YOUR brand colors.
            Every inquiry goes directly to you. No competitors in sight.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Everything you need
          </h2>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Image} title="Photo Gallery" description="Beautiful grid layout showcasing all your staged photos with lightbox view." />
            <FeatureCard icon={Users} title="Lead Capture" description="Built-in contact forms. Every inquiry syncs to your Flow dashboard." />
            <FeatureCard icon={Share2} title="One-Click Share" description="Perfect for Instagram bio, Facebook posts, and email signatures." />
            <FeatureCard icon={Sparkles} title="Your Branding" description="Your photo, logo, brand colors, and contact info front and center." />
            <FeatureCard icon={Globe} title="Custom URLs" description="Clean, memorable links like flow.link/123-main-street" />
            <FeatureCard icon={MessageSquare} title="Property Details" description="Beds, baths, price, description - everything buyers want to know." />
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            The difference is clear
          </h2>

          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <div className="rounded-2xl border-2 border-red-900/50 bg-red-950/30 p-8">
              <h3 className="text-xl font-semibold text-red-400">Zillow Links</h3>
              <ul className="mt-4 space-y-3 text-red-300">
                <li>❌ Other agents displayed</li>
                <li>❌ Competing listings shown</li>
                <li>❌ Leads sold to multiple agents</li>
                <li>❌ No branding control</li>
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-emerald-900/50 bg-emerald-950/30 p-8">
              <h3 className="text-xl font-semibold text-emerald-400">Flow Pages</h3>
              <ul className="mt-4 space-y-3 text-emerald-300">
                <li>✓ 100% your branding</li>
                <li>✓ Zero competitors</li>
                <li>✓ Leads go only to you</li>
                <li>✓ Full customization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

// ============================================================================
// PORTFOLIO FEATURE PAGE
// ============================================================================
export const PortfolioFeaturePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-24 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(236,72,153,0.15),_transparent_40%)]" />

        <div className="relative mx-auto max-w-7xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
              <Award className="h-4 w-4 text-pink-400" />
              Your Professional Presence
            </div>

            <h1 className="mt-8 text-5xl font-semibold leading-tight text-white md:text-7xl">
              One link.
              <span className="block text-pink-400">All your listings.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/60">
              Your branded agent portfolio page showcasing every active listing.
              Perfect for your Instagram bio, email signature, and business cards.
            </p>

            <div className="mt-10">
              <a href="/?login=true" className="rounded-full bg-pink-500 px-8 py-4 text-lg font-semibold text-white transition-transform hover:-translate-y-1">
                Build Your Portfolio
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-semibold text-white">
            Your brand deserves better.
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            You're not just another agent. You're building a brand. But every time you share
            a Zillow link, you're promoting their brand instead of yours.
          </p>

          <div className="my-12 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

          <h2 className="text-4xl font-semibold text-pink-400">
            Your portfolio. Your brand.
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            A beautiful, mobile-optimized page featuring your photo, your branding,
            and every one of your active listings. One link for everything.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Everything in your portfolio
          </h2>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Users} title="Agent Header" description="Your photo, name, title, and brokerage prominently displayed." />
            <FeatureCard icon={Home} title="Listing Grid" description="All your active listings in a beautiful, browsable grid." />
            <FeatureCard icon={Globe} title="Contact Info" description="Phone, email, and social links for easy client contact." />
            <FeatureCard icon={Sparkles} title="Brand Colors" description="Custom accent colors to match your personal brand." />
            <FeatureCard icon={Share2} title="Easy Sharing" description="One memorable link for bio, cards, and signatures." />
            <FeatureCard icon={TrendingUp} title="Lead Tracking" description="See which listings get the most interest." />
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Use it everywhere
          </h2>

          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {[
              "Instagram bio link",
              "Facebook profile",
              "Email signature",
              "Business cards",
              "LinkedIn profile",
              "Text messages",
              "Open house signs",
              "Marketing materials"
            ].map((use) => (
              <div key={use} className="flex items-center gap-3 rounded-xl bg-slate-800/50 border border-slate-700 p-4">
                <CheckCircle2 className="h-5 w-5 text-pink-400" />
                <span className="font-medium text-white">{use}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

// ============================================================================
// REFER & EARN FEATURE PAGE
// ============================================================================
export const ReferEarnFeaturePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <FeatureNavigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-24 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(20,244,204,0.15),_transparent_40%)]" />

        <div className="relative mx-auto max-w-7xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
              <Gift className="h-4 w-4 text-[#5eead4]" />
              Earn While You Work
            </div>

            <h1 className="mt-8 text-5xl font-semibold leading-tight text-white md:text-7xl">
              5,000 credits.
              <span className="block text-[#5eead4]">Per referral.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-xl text-white/60">
              Know someone who needs a mortgage? Refer them to our lending team.
              When they close, you earn 5,000 staging credits. It's that simple.
            </p>

            <div className="mt-10">
              <a href="/?login=true" className="rounded-full bg-[#5eead4] px-8 py-4 text-lg font-semibold text-[#0a0f1a] transition-transform hover:-translate-y-1">
                Start Referring
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Simple as 1-2-3
          </h2>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              { step: "1", icon: Users, title: "Submit a Lead", desc: "Enter your client's info through your Flow dashboard. Takes 30 seconds." },
              { step: "2", icon: MessageSquare, title: "We Take Over", desc: "Our lending team contacts them, handles everything, and keeps you updated." },
              { step: "3", icon: Gift, title: "Earn Credits", desc: "When they close, 5,000 credits hit your account automatically." }
            ].map((item) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#5eead4]/10 text-3xl font-bold text-[#5eead4]">
                  {item.step}
                </div>
                <item.icon className="mx-auto mt-6 h-8 w-8 text-[#5eead4]" />
                <h3 className="mt-4 text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonus Tiers */}
      <section className="bg-[#0a0f1a] px-6 py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Bonus milestones for top referrers
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-slate-400">
            The more you refer, the more you earn
          </p>

          <div className="mt-12 space-y-4">
            {[
              { refs: "5", bonus: "10,000", total: "35,000" },
              { refs: "10", bonus: "25,000", total: "75,000" },
              { refs: "25", bonus: "75,000", total: "200,000" }
            ].map((tier) => (
              <div key={tier.refs} className="flex items-center justify-between rounded-2xl bg-slate-800/50 border border-slate-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#5eead4]/10">
                    <TrendingUp className="h-6 w-6 text-[#5eead4]" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{tier.refs} Referrals</div>
                    <div className="text-sm text-slate-400">+{tier.bonus} bonus credits</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#5eead4]">{tier.total}</div>
                  <div className="text-sm text-slate-400">total credits</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-24 bg-[#0d1420]">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-4xl font-semibold text-white">
            Common questions
          </h2>

          <div className="mt-12 space-y-6">
            {[
              { q: "What qualifies as a referral?", a: "Any buyer or refinance lead you submit through your dashboard. They don't need to be your client." },
              { q: "When do I get my credits?", a: "Credits are deposited to your account within 48 hours of loan closing." },
              { q: "Is there a limit?", a: "No limit. Refer as many as you want. Top agents earn hundreds of thousands of credits per year." },
              { q: "What can I use credits for?", a: "Virtual staging, video reels, landing pages - everything in Flow. Credits never expire." }
            ].map((item) => (
              <div key={item.q} className="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
                <h3 className="font-semibold text-white">{item.q}</h3>
                <p className="mt-2 text-slate-400">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureFooter />
    </div>
  );
};

export default {
  VirtualStagingFeaturePage,
  VideoReelsFeaturePage,
  BatchProcessingFeaturePage,
  LandingPagesFeaturePage,
  PortfolioFeaturePage,
  ReferEarnFeaturePage
};
